import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { Form } from 'features/common/components/Form';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupDateTimePicker } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupDateTimePicker/PunchupDateTimePicker';
import { PunchupDivider } from 'features/common/components/Punchup/primitives/layout/PunchupDivider';
import { PunchupFormToggle } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFormToggle';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { PunchupRichTextField } from 'features/common/components/Punchup/primitives/forms/fields/PunchupRichTextField';
import { TimezoneSelect } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupTimeZone';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { PunchupSeatsChartPicker } from 'features/common/components/Punchup/composites/seats/PunchupSeatsChartPicker';
import { SeatsChartChangePreviewPanel } from 'features/common/components/Punchup/composites/seats/SeatsChartChangePreviewPanel';
import { PunchupDangerZone } from 'features/common/components/Punchup/composites/forms/PunchupDangerZone';
import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';
import {
	PunchupNotifyDateChangeDialog,
	buildDateChangePreview,
} from 'features/common/components/Punchup/composites/dialogs/PunchupNotifyDateChangeDialog';
import { SeatsChart } from 'services/Seats/types';
import { convertDateWithTimezoneToUTC } from 'utils/utils';
import { EVENT_MODE_PUNCHUP } from 'utils/constants';
import { getFormDetails, handleInitialValues } from 'features/PunchupPages/EditEvent/utils';
import { Event, DefaultValuesTypes } from 'features/PunchupPages/EditEvent/types';

import {
	useAccordionForm,
	PunchupAccordionSubSectionGroup,
	PunchupAccordionSubSection,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { TagsSection } from 'features/Dashboard/components/punchup/TagsSection';
import {
	ADJUST_SHOW_DATES_INFO,
	SHOW_SCHEDULE_HELP,
	persistedScheduleOf,
	useShowScheduleSync,
} from 'features/common/hooks/useShowScheduleSync';
import { useNotifyAttendeesOnDateChange } from 'features/common/hooks/useNotifyAttendeesOnDateChange';
import { useGeneralSection } from './useGeneralSection';
import styles from './GeneralSection.module.css';

// ---------------------------------------------------------------------------
// Inner form — only rendered when event is available
// ---------------------------------------------------------------------------

interface GeneralSectionFormProps {
	event: Event;
	editEvent: (variables: any, options?: any) => Promise<Event>;
	eventId: string;
}

const GeneralSectionForm: FC<GeneralSectionFormProps> = ({ event, editEvent, eventId }) => {
	const { t } = useTranslation();

	const [selectedChart, setSelectedChart] = useState<SeatsChart | null>(null);
	const [originalChartKey] = useState<string | undefined>(event.seats_chart_key);
	const [manualChartMode, setManualChartMode] = useState(false);
	const [showChartConfirm, setShowChartConfirm] = useState(false);
	const [chartPreviewLoading, setChartPreviewLoading] = useState(false);
	const [chartPreviewError, setChartPreviewError] = useState(false);
	// Holds the submitted form values while the notify-attendees confirm dialog is open (date changed).
	const [pendingSave, setPendingSave] = useState<DefaultValuesTypes | null>(null);
	const { notifyAsync } = useNotifyAttendeesOnDateChange();
	const [originalDates] = useState({
		startDate: event.days[0]?.start_date,
		endDate: event.days[0]?.end_date,
	});

	const [initialValues] = useState<DefaultValuesTypes>(() => handleInitialValues(event));

	const formMethods = useForm<DefaultValuesTypes>({
		defaultValues: initialValues,
		resolver: getFormDetails(EVENT_MODE_PUNCHUP),
	});

	const { close, flash, scrollToSection, isSaving } = useAccordionForm(formMethods);

	const { handleStartDateChange, revalidateScheduling } = useShowScheduleSync(formMethods, persistedScheduleOf(event));

	const startDate = formMethods.watch('dates.0.start_date');
	const doorTime = formMethods.watch('door_time');
	const changePassword = formMethods.watch('change_password');
	const wantToChangePassword = formMethods.watch('want_to_change_password');
	const hideEvent = formMethods.watch('hide_event');
	const hasPassword = event?.has_password;
	const seatsChartKey = formMethods.watch('seats_chart_key');
	const timeZone = formMethods.watch('timezone');

	const datesHaveChanged = startDate && convertDateWithTimezoneToUTC(startDate, timeZone) !== originalDates.startDate;

	useEffect(() => {
		if (hasPassword && !changePassword) {
			formMethods.setValue('remove_password', true);
		} else {
			formMethods.setValue('remove_password', false);
		}
	}, [changePassword, hasPassword, formMethods]);

	useEffect(() => {
		if (!hasPassword && changePassword) {
			formMethods.setValue('want_to_change_password', true);
		} else if (!hasPassword && !changePassword) {
			formMethods.setValue('want_to_change_password', false);
		}
	}, [changePassword, hasPassword, formMethods]);

	const saveEvent = async (values: DefaultValuesTypes, notify: boolean, customMessage = '') => {
		try {
			await editEvent(
				{ values, id: eventId },
				{
					onSuccess: () => {
						// isDirty clears so the unsaved-dot disappears next render. event-updated +
						// event-name-updated are posted by useGeneralSection's mutation onSuccess — not here.
						formMethods.reset(values);
						flash('success', {
							title: t('generalSection-saved', 'Show details saved'),
							onComplete: () => {
								close();
								window.parent.postMessage({ action: 'scroll-to-top' }, '*');
							},
						});
						// Notify runs only after a successful save. Its failure is non-blocking — the show is
						// already saved; report it as a separate flash so it doesn't read as a save failure.
						if (notify) {
							notifyAsync({ eventIds: [eventId], customMessage })
								.then(() =>
									flash('success', { title: t('editEvent-notify-sent', 'Attendees notified of the date change') })
								)
								.catch(() =>
									flash('error', {
										title: t('editEvent-notify-failed', "Show saved, but we couldn't notify attendees"),
									})
								);
						}
					},
					onError: () => flash('error', { title: t('generalSection-save-failed', 'Failed to save show details') }),
				}
			);
		} catch {
			// Error handled by mutation's onError
		}
	};

	const onSubmit = (values: DefaultValuesTypes) => {
		// Changing the show date affects everyone holding a ticket — offer to email them. Otherwise save directly.
		if (datesHaveChanged) {
			setPendingSave(values);
			return;
		}
		void saveEvent(values, false);
	};

	const onCancel = () => {
		formMethods.reset(initialValues); // clears isDirty
		scrollToSection('start');
		close();
	};

	// Frozen once any ticket has sold — switching the chart would invalidate existing seat assignments.
	// Summed from per-ticket-type `sold`; the event-level `tickets_sold` is not populated on this payload.
	const soldExists = (event.ticket_types ?? []).reduce((total, tt) => total + (tt.sold ?? 0), 0) > 0;
	// The lock is lifted once the manager opts into manual chart-change mode via the Danger Zone confirm,
	// mirroring the Seats accordion's manual-mode gate. Red zone has no hall-change UI; the green Edit Event
	// sub-page allows the change behind the same impact preview — this gate brings the accordion in line.
	const chartsLocked = soldExists && !manualChartMode;

	const activateManualChartMode = () => {
		setManualChartMode(true);
		setShowChartConfirm(false);
	};

	const exitManualChartMode = () => {
		setManualChartMode(false);
		// Revert any unconfirmed pick so an un-saved chart change cannot persist on exit.
		setSelectedChart(null);
		formMethods.setValue('seats_chart_key', originalChartKey);
		// Clear the preview Save-gate flags so locking after a failed/loading preview can't strand Save
		// disabled (the panel also clears them on unmount; this is the explicit lock-path reset).
		setChartPreviewLoading(false);
		setChartPreviewError(false);
	};

	const handleChartSelect = (chart: SeatsChart) => {
		if (chartsLocked) return;
		if (selectedChart?.key === chart.key || seatsChartKey === chart.key) {
			setSelectedChart(null);
			formMethods.setValue('seats_chart_key', undefined);
		} else {
			setSelectedChart(chart);
			formMethods.setValue('seats_chart_key', chart.key);
		}
	};

	return (
		<Form formMethods={formMethods} onSubmit={onSubmit}>
			<div className="entityEditSavingWrapper">
				<div className="entityEditBody">
					{/* ── Show identity ─────────────────────────────────────────── */}

					<PunchupInput id="name" name="name" label={t('general-show-name', 'Show Name')} required />

					<PunchupRichTextField id="description" name="description" label={t('general-description', 'Description')} />

					<PunchupInput id="venue" name="venue" label={t('general-venue-name', 'Venue Name')} disabled />

					<PunchupInput id="location" name="location" label={t('general-venue-address', 'Venue Address')} disabled />

					<p className="punchupTextMeta">
						{t('general-venue-readonly-note', 'Venue name and address come from your venue configuration.')}
					</p>

					<PunchupDivider />

					{/* ── Scheduling ────────────────────────────────────────────── */}

					{/* Order: Show Start → Show End → Open Doors. A manager reasons start, then end, then doors;
					    and the door field's start-bounded limits are only live once a start has been entered. */}
					<PunchupDateTimePicker
						id="startDate"
						name="dates[0].start_date"
						label={t('general-start-date', 'Show Start Date')}
						required
						help={t('general-start-date-help', SHOW_SCHEDULE_HELP.startDate)}
						onChange={handleStartDateChange}
						minDate={new Date()}
					/>

					<PunchupDateTimePicker
						id="endDate"
						name="dates[0].end_date"
						label={t('general-end-date', 'Show End Date')}
						required
						help={t('general-end-date-help', SHOW_SCHEDULE_HELP.endDate)}
						onChange={revalidateScheduling}
						minDate={startDate}
					/>

					<PunchupDateTimePicker
						id="doorTime"
						name="door_time"
						label={t('general-open-doors', 'Open doors')}
						required
						help={t('general-open-doors-help', SHOW_SCHEDULE_HELP.openDoors)}
						onChange={revalidateScheduling}
						maxDate={startDate}
						minTime={new Date(0, 0, 0, 0, 0, 0)}
						maxTime={
							// Constrain to the show-start time only when startDate is a valid Date sharing the day
							// with door_time; otherwise fall back to end-of-day (guards an unset/invalid start).
							doorTime &&
							startDate &&
							!isNaN(startDate.getTime()) &&
							DateTime.fromJSDate(startDate).hasSame(DateTime.fromJSDate(doorTime), 'day')
								? DateTime.fromJSDate(startDate).toJSDate()
								: new Date(0, 0, 0, 23, 59, 59)
						}
					/>

					<FlexContainer alignItems="center">
						<PunchupFormToggle
							htmlFor="on_sale"
							name="on_sale"
							label={t('general-on-sale', 'On sale')}
							info={t(
								'general-on-sale-info',
								'When off, the show stays visible but tickets are not for sale — buyers see a "not on sale" message'
							)}
						/>
					</FlexContainer>

					<FlexContainer alignItems="center">
						<PunchupFormToggle
							htmlFor="inactive"
							name="inactive"
							label={t('general-unpublished', 'Unpublished')}
							info={t(
								'general-unpublished-info',
								'When on, the show is hidden from every public page and cannot be purchased anywhere. It stays visible here in the dashboard.'
							)}
						/>
					</FlexContainer>

					<FlexContainer alignItems="center">
						<PunchupFormToggle
							htmlFor="hide_event"
							name="hide_event"
							label={t('general-hide-show', 'Hide show')}
							info={t('general-hide-show-info', "The show won't be publicly visible until the date you specify")}
						/>
					</FlexContainer>

					{hideEvent && (
						<PunchupDateTimePicker
							id="hidden_until"
							name="hidden_until"
							label={t('general-hidden-until', 'Hidden until')}
							required
							minDate={new Date()}
						/>
					)}

					<FlexContainer alignItems="center">
						{/* The field name is the API payload (`adjust_end_date`); the label is what it does now. */}
						<PunchupFormToggle
							htmlFor="adjust_end_date"
							name="adjust_end_date"
							label={t('general-adjust-show-dates', 'Adjust show dates')}
							info={t('general-adjust-show-dates-info', ADJUST_SHOW_DATES_INFO)}
							help={SHOW_SCHEDULE_HELP.adjustShowDates}
						/>
					</FlexContainer>

					{datesHaveChanged && (
						<FlexContainer alignItems="center">
							<PunchupFormToggle
								htmlFor="adjust_ticket_dates"
								name="adjust_ticket_dates"
								label={t('general-adjust-ticket-dates', 'Adjust ticket selling dates')}
								info={t(
									'general-adjust-ticket-dates-info',
									'Automatically shifts ticket on-sale and off-sale dates to match the new show dates'
								)}
							/>
						</FlexContainer>
					)}

					<TimezoneSelect eventTimezone={event.time_zone || 'America/New_York'} />

					<PunchupDivider />

					{/* ── Access ────────────────────────────────────────────────── */}

					<FlexContainer alignItems="center">
						<PunchupFormToggle
							htmlFor="change_password"
							name="change_password"
							label={t('general-protect-show', 'Protect show')}
							info={t('general-protect-show-info', 'Require a password to view and purchase tickets for this show')}
						/>
					</FlexContainer>

					{changePassword && hasPassword && (
						<FlexContainer alignItems="center">
							<PunchupFormToggle
								htmlFor="want_to_change_password"
								name="want_to_change_password"
								label={t('general-change-password', 'Change password')}
							/>
						</FlexContainer>
					)}

					{((changePassword && !hasPassword) || (changePassword && hasPassword && wantToChangePassword)) && (
						<>
							<PunchupInput
								id="password"
								label={t('general-password', 'Password')}
								name="password"
								placeholder={t('general-password-placeholder', 'Enter password')}
								type="password"
								required
							/>
							<PunchupInput
								id="confirm_password"
								label={t('general-confirm-password', 'Confirm password')}
								name="confirm_password"
								placeholder={t('general-confirm-password-placeholder', 'Confirm password')}
								type="password"
								required
							/>
						</>
					)}

					{seatsChartKey && (
						<>
							<PunchupSeatsChartPicker
								onChartSelect={handleChartSelect}
								selectedChartId={seatsChartKey || selectedChart?.key}
								title={t('general-seat-chart-title', 'Select Seat Chart')}
								description={t('general-seat-chart-desc', 'Choose the seating chart for this show.')}
								locked={chartsLocked}
							/>

							<FlexContainer alignItems="center">
								<PunchupFormToggle
									htmlFor="enforce_no_orphan_seats"
									name="enforce_no_orphan_seats"
									label={t('general-no-orphan-seats', 'Prevent single empty seats')}
									info={t(
										'general-no-orphan-seats-info',
										'When on, fans cannot make a selection that leaves a lone empty seat between taken seats in a row'
									)}
								/>
							</FlexContainer>

							{soldExists && (
								<PunchupDangerZone
									title={t('general-seat-chart-danger-title', 'Danger Zone')}
									description={t(
										'general-seat-chart-danger-desc',
										'This show has sold tickets. Changing the seating chart can move buyers to new seats or leave some seats unmatched.'
									)}
									warning={
										manualChartMode
											? t(
													'general-seat-chart-danger-active',
													'Manual chart change is on — review the impact below before saving.'
											  )
											: undefined
									}
									actions={
										manualChartMode ? (
											<PunchupButton
												type="button"
												variant="secondary"
												onClick={exitManualChartMode}
												text={t('general-seat-chart-lock', 'Lock seating chart')}
											/>
										) : (
											<PunchupButton
												type="button"
												variant="destructive"
												icon={<AlertTriangle size={16} />}
												onClick={() => setShowChartConfirm(true)}
												text={t('general-seat-chart-unlock', 'Change seating chart')}
											/>
										)
									}
								/>
							)}

							{manualChartMode && originalChartKey && seatsChartKey !== originalChartKey && (
								<SeatsChartChangePreviewPanel
									eventId={eventId}
									newChartKey={seatsChartKey}
									onLoadingChange={setChartPreviewLoading}
									onErrorChange={setChartPreviewError}
								/>
							)}

							{showChartConfirm &&
								createPortal(
									<PunchupConfirmDialog
										icon={<AlertTriangle size={24} />}
										iconVariant="destructive"
										title={t('general-seat-chart-confirm-title', 'Change the seating chart?')}
										text={t(
											'general-seat-chart-confirm-text',
											'This show has sold tickets. Changing the chart can move buyers to new seats or leave some seats unmatched (you may need to reassign those manually). Only proceed if you know what you are doing.'
										)}
										buttons={[
											{
												label: t('button-cancel', 'Cancel'),
												variant: 'secondary',
												onClick: () => setShowChartConfirm(false),
											},
											{
												label: t('general-seat-chart-confirm-cta', 'Enable chart change'),
												variant: 'destructive',
												icon: <AlertTriangle size={16} />,
												onClick: activateManualChartMode,
											},
										]}
									/>,
									document.body
								)}
						</>
					)}

					{/* ── Actions ───────────────────────────────────────────────── */}

					<FlexContainer justifyContent="flex-end" gap="12px" className="entityEditActions">
						<PunchupButton type="button" variant="secondary" onClick={onCancel} text={t('button-cancel')} />
						<PunchupButton
							type="submit"
							variant="primary"
							text={t('button-save')}
							// Save is never disabled by a validation error — pressing it names the problem instead.
							// It waits only on an unresolved seats-chart impact preview, whose effect is unknown.
							disabled={chartPreviewLoading || chartPreviewError}
							isLoading={isSaving}
						/>
					</FlexContainer>
				</div>
				{isSaving && (
					<div className="entityEditSavingOverlay">
						<PunchupSpinner size={32} />
					</div>
				)}
			</div>

			{pendingSave &&
				createPortal(
					<PunchupNotifyDateChangeDialog
						preview={buildDateChangePreview({
							eventName: pendingSave.name,
							eventId,
							startDate: pendingSave.dates?.[0]?.start_date,
							endDate: pendingSave.dates?.[0]?.end_date,
							timeZone: pendingSave.timezone,
							venueName: pendingSave.venue,
							primaryColor: event.event_webpage_settings?.theme_colors,
							eventBanner: event.event_webpage_settings?.event_banner,
						})}
						onSkip={() => {
							const values = pendingSave;
							setPendingSave(null);
							void saveEvent(values, false);
						}}
						onConfirm={(customMessage) => {
							const values = pendingSave;
							setPendingSave(null);
							void saveEvent(values, true, customMessage);
						}}
					/>,
					document.body
				)}
		</Form>
	);
};

// ---------------------------------------------------------------------------
// Outer shell — guards until event data is available, then hosts the show-details form and Show Tags.
// The sub-section group stays because both report dirty independently (details via useAccordionForm, tags
// via PunchupTagManager's onDirtyChange): one shared section context would let either clear the other's.
// ---------------------------------------------------------------------------

const GeneralSection: FC = () => {
	const { t } = useTranslation();
	const { event, editEvent, eventId } = useGeneralSection();

	if (!event) return null;

	return (
		<PunchupAccordionSubSectionGroup>
			<PunchupAccordionSubSection id="details">
				<GeneralSectionForm event={event} editEvent={editEvent} eventId={eventId} />
			</PunchupAccordionSubSection>

			<PunchupDivider />
			<div className={styles.subPanelHeader}>
				<h3 className={styles.subPanelTitle}>{t('tags-event-tags-green', 'Show Tags')}</h3>
				<p className={styles.subPanelDescription}>
					{t('dashboard-tags-description', 'Help guests find this show in search and on related-show panels.')}
				</p>
			</div>
			<PunchupAccordionSubSection id="tags">
				<TagsSection />
			</PunchupAccordionSubSection>
		</PunchupAccordionSubSectionGroup>
	);
};

export { GeneralSection };
